import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Button, Typography, Stack } from '@mui/material';
import MemeContent from './MemeContent';
// import data from './data.json';
import MemeReply from './MemeReply';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { CommentType, FlattenedFeedType, PaginatedComments, PostType } from '../memeverse_types';
import { getCommentsOnPost, getPostDetails } from '../../../utils/apis/memeverse';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { AblyWebsocketChannelProvider } from '../../../component/AblyWebsockets/AblyWebsocketChannelProvider';
import Lottie from 'lottie-react';
import LoadingFace from '../../../assets/animations/LoadingFace.json'
import DetectBottomScrollDiv from '../../journaling/DetectBottomScrollDiv';
import { flattenCommentsList } from '../helper';
import { useAuth } from '../../../hooks/useAuth';
import { useAskWebsockets } from '../hooks/useAskWebsockets';

const PostDetailPage = () => {
  return (
      <AblyWebsocketChannelProvider channelName='ask'>
        <MainPostDetailPage />
      </AblyWebsocketChannelProvider>
    )
}

const MainPostDetailPage = () => {
  const [ post, setPost ] = useState<PostType>(null);

  const location = useLocation();
  const navigate = useNavigate();
  const { docId } = useParams();
  const { authUser } = useAuth();

  const { refresh } = useAskWebsockets(['memeDetails', docId])
  
  // Refs
  const memeReplyRefs = useRef<{[commentId: string]: HTMLDivElement | null}>({});
  
  const scrollToComment = (commentId: string) => {
    const ref = memeReplyRefs.current[commentId];
    setTimeout(() => {
      const ref = memeReplyRefs.current[commentId];
      if (ref) {
        ref.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 500);
  };

  useEffect(() => {
    if (docId) {
      getPostDetails(authUser?.uid, docId)
        .then(async (resp) => {
          if (resp.ok) {
            const data = await resp.json();
            setPost({...data, docId: docId, flat_comments_list: []});
          } else {
            console.error('Failed to fetch post details');
            navigate('/') // reroute user if can't find post
          }
        })
        .catch((error) => {
          console.error('Error fetching post details:', error);
          navigate('/') // reroute user if can't find post
        });
    }
  }, [docId]);

  const {
    data: commentList,
    fetchNextPage: fetchNextMemeComments,
    hasNextPage: hasNextMemeComments,
    isFetching: isFetchingMemeComments,
    isFetchingNextPage: isFetchingNextMemeComments,
  } = useInfiniteQuery({
    queryKey: ['memeDetails', post?.docId],
    queryFn: async ({pageParam}) => {
      if (!post?.docId) {
        return {
          pagination: {},
          posts: {},
          comments: {},
          orderedPosts: [],
          orderedComments: [],
        } as FlattenedFeedType;
      }
      return await getCommentsOnPost(authUser?.uid, post.docId, 10, pageParam)
        .then(async (resp) => {
          if (resp.ok) {
            let data: PaginatedComments = await resp.json();

            let posts = {} as Record<string, PostType>;
            let comments = {} as Record<string, CommentType>;
            let orderedPosts = [] as string[];
            let orderedComments = [] as string[];

            // store main post on page 0
            if (pageParam === 1) {
              posts[post.docId] = {...post};
              orderedPosts.push(post.docId)
            }

            // flatten comments list
            flattenCommentsList(
              comments,
              data.comments,
              pageParam
            )

            for (let comment of data.comments) {
              orderedComments.push(comment.docId);
            }

            return {
              pagination: data.pagination,
              posts,
              comments,
              orderedPosts,
              orderedComments
            } as FlattenedFeedType;

          } else {
            return {
              pagination: {},
              posts: {},
              comments: {},
              orderedPosts: [],
              orderedComments: [],
            } as FlattenedFeedType
          }
        }).catch(err => {
          console.log(err)
          return {
            pagination: {},
            posts: {},
            comments: {},
            orderedPosts: [],
            orderedComments: [],
          } as FlattenedFeedType
        })
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (!lastPage.pagination.has_next) {
        return undefined
      }
      return lastPage.pagination.next_page
    },
    getPreviousPageParam: (firstPage) => {
      if (!firstPage.pagination.has_next) {
        return undefined
      }
      return firstPage.pagination.next_page
    },
    enabled: !!post?.docId,
  })

  const loadNextPage = () => {
    if (!isFetchingMemeComments && !isFetchingNextMemeComments && hasNextMemeComments) {
      fetchNextMemeComments();
    }
  }

  const { allPosts, allComments, orderedComments } = useMemo(() => {
    const allPosts: Record<string, PostType> = {};
    const allComments: Record<string, CommentType> = {};
    let orderedComments: string[] = [];

    if (commentList?.pages) {
      commentList.pages.forEach(page => {
        Object.assign(allComments, page.comments);
        orderedComments = [...orderedComments, ...page.orderedComments];
      })

      if (post) {
        allPosts[post.docId] = {
          ...commentList.pages[0].posts[post.docId],
          flat_comments_list: orderedComments,
        }
      }
    }

    return { allPosts, allComments, orderedComments };
  }, [commentList, refresh])

  if (!post) {
    return <></>
  } else if (!allPosts[post.docId]) {
    return <></>
  }

  return (
    <Box
      className="meme"
      sx={{
        maxWidth: '800px',
        margin: 'auto',
        padding: {xs: 1, md: 2.5},
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: {xs: 1, md: 2} }}>
        <Button
          sx={{ minWidth: '40px', padding: '6px' }}
          onClick={() => {
            if (location.state?.from && location.state?.scrollY) {
              navigate(location.state.from, {state: {
                scrollY: location.state.scrollY,
                tabValue: location.state.tabValue || 0,
              }});
            } else {
              navigate(-1)
            }
          }}
        >
          <ArrowBackIcon />
        </Button>
        <Typography variant="h6">Post Details</Typography>
      </Stack>

      {/* Display the Meme Content */}
      {!!allPosts[post.docId] &&
        <>
          <MemeContent
            post={allPosts[post.docId]}
            isDetailsPage={true}
            scrollToComment={scrollToComment}
          />
          {
            !!orderedComments && orderedComments.map(
              (commentId: string) => (
                <MemeReply
                  key={`comment-${commentId}`}
                  parentPostOrComment={post}
                  reply={allComments[commentId]}
                  showNested={true}
                  shouldLimitLength={false}
                  allComments={allComments}
                  memeReplyRefs={memeReplyRefs}
                  scrollToComment={scrollToComment}
                />
              ))
          }

          {
            (isFetchingMemeComments || isFetchingNextMemeComments) &&
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                '& path': {
                  fill: (theme) => theme.palette.primary.main
                }
              }}
            >
              <Lottie
                animationData={LoadingFace}
                loop={true}
                style={{
                  width: '100px',
                  height: '100px',
                }}
              />
            </Box>
          }
          <DetectBottomScrollDiv
            onReachBottom={loadNextPage}
            isLoading={isFetchingMemeComments || isFetchingNextMemeComments}
            isFullyLoaded={!hasNextMemeComments}
            rootMargin='400px'
            className='infinite-scroll-bottom'
          />

        </>
      }

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          position: 'absolute',
          bottom: {xs: 56, md: 0},
          left: {xs: 0, md: 250},
          right: {xs: 0, sm: 10},
          background: (theme) => theme.palette.background.default,
        }}
      >
        <Typography
          variant="body2" color="text.secondary"
          textAlign={'center'}
          sx={{p: 1}}
        >
          Soopra AI agents: Beta phase, no affiliation, without endorsement.
        </Typography>
      </Box>
    </Box>
  );
};

export default PostDetailPage;
